import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import React from "react";
import PhoneInput, {
  isValidPhoneNumber,
  Value,
} from "react-phone-number-input";
import sv from "react-phone-number-input/locale/sv.json";
import "react-phone-number-input/style.css"; // Standard CSS för PhoneInput

import {
  ContactPersonRolesEnum,
  ContactPersonRolesEnumLabel,
  NewCompanyCommunication,
  usePostNewCompanyCommunicationMutation,
} from "../../../store/services/communicationService";
import PhoneTextInput from "./components/PhoneNumber";
interface NewCompanyContactProps {
  open: boolean;
  handleClose: () => void;
  customerNo: number;
}

export default function NewCompanyContact(
  props: NewCompanyContactProps
): React.JSX.Element {
  const { open, handleClose, customerNo } = props;
  const theme = useTheme();
  const [firstName, setFirstname] = React.useState("");
  const [lastName, setLastname] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [role, setRole] = React.useState<number[]>([]);
  const [responsible, setResponsible] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const ArrayOfRoles = Object.keys(ContactPersonRolesEnum)
    .filter((key: string) =>
      isNaN(
        Number(
          ContactPersonRolesEnum[key as keyof typeof ContactPersonRolesEnum]
        )
      )
    )
    .map((x) => +x);

  const handleRoleChange = (event: SelectChangeEvent<number[]>) => {
    setRole(event.target.value as number[]);
  };

  const [postNewContact, { isLoading }] =
    usePostNewCompanyCommunicationMutation();

  const handleValueChange = (newValue: Value | string | undefined) => {
    setPhone(newValue ?? "");
  };

  const handleSave = () => {
    const data: NewCompanyCommunication = {
      ContactPersonId: null,
      Firstname: firstName,
      Lastname: lastName,
      Email: email,
      Phone: phone,
      Roles: role,
      IsResponsible: responsible,
      Communications: [],
    };
    postNewContact({ customerId: customerNo, data })
      .unwrap()
      .then(() => {
        handleClose();
      })
      .catch((err) => {
        if (err.data && typeof err.data === "string") {
          if (err.data.includes("Domain error")) {
            setError(
              "Kontot har blivit skapat men du har angivit en ogiltig epostadress, kontrollera att epostadressen är korrekt."
            );
          } else {
            setError(err.data);
          }
        } else {
          setError(err.data);
        }
      });
  };

  const isFormInvalid =
    (phone.length > 0 && !isValidPhoneNumber(phone ?? "")) ||
    (email.length > 0 && !validateEmail(email)) ||
    lastName.length == 0 ||
    firstName.length == 0 ||
    isLoading ||
    error !== null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.light }}>
        Ny företagskontakt
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ paddingTop: "25px" }}>
          <TextField
            autoFocus
            margin="dense"
            size="small"
            id="name"
            label="Förnamn"
            type="text"
            onChange={(e) => setFirstname(e.target.value)}
            fullWidth
            variant="outlined"
          />
          <TextField
            margin="dense"
            size="small"
            id="lastname"
            label="Efternamn"
            type="text"
            onChange={(e) => setLastname(e.target.value)}
            fullWidth
            variant="outlined"
          />
          <TextField
            margin="dense"
            size="small"
            id="email"
            label="Epost"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            variant="outlined"
          />
          <PhoneInput
            labels={sv}
            countries={["SE", "NO", "FI", "DE", "DK"]}
            autoFocus
            defaultCountry="SE"
            placeholder="Telefonnummer"
            sx={{ width: 120 }}
            value={phone ?? ""}
            inputComponent={PhoneTextInput}
            onChange={handleValueChange}
            error={Boolean(
              phone && phone.length > 0 && !isValidPhoneNumber(phone)
            )}
            helperText={
              isValidPhoneNumber(phone ?? "") || (phone && phone.length == 0)
                ? undefined
                : "Ogiltigt telefonnummer"
            }
          />

          <FormControl fullWidth>
            <InputLabel>Roller</InputLabel>
            <Select
              multiple
              value={role}
              input={<OutlinedInput label="Roller" />}
              onChange={handleRoleChange}
              renderValue={(selected) =>
                selected
                  .map(
                    (x) =>
                      ContactPersonRolesEnumLabel[x as ContactPersonRolesEnum]
                  )
                  .join(", ")
              }
            >
              {ArrayOfRoles.map((name, idx) => (
                <MenuItem key={idx} value={name}>
                  <Checkbox
                    checked={role.find((x) => x === name) !== undefined}
                  />
                  <ListItemText
                    primary={
                      ContactPersonRolesEnumLabel[
                        name as ContactPersonRolesEnum
                      ]
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onClick={() => setResponsible(!responsible)}
                  checked={responsible}
                />
              }
              label="Avtalsansvarig"
            />
          </FormGroup>
        </Stack>
      </DialogContent>
      {!error ? (
        <DialogActions>
          <Button color="secondary" variant="outlined" onClick={handleClose}>
            Avbryt
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={14} /> : null}
            onClick={handleSave}
            disabled={isFormInvalid}
          >
            Skapa
          </Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button color="secondary" variant="outlined" onClick={handleClose}>
            Ok
          </Button>
        </DialogActions>
      )}

      {error && <Alert severity="error">{error}</Alert>}
    </Dialog>
  );
}
const validateEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};
